/* ============================================================
   ShoLi — Confirm Dialog Module
   Async confirmation dialog (no external dependencies)
   ============================================================ */

type ConfirmDialogApi = {
  init: () => void;
  ask: (message: string) => Promise<boolean>;
};

interface PendingResolve {
  resolve: (value: boolean) => void;
  reject: (reason?: unknown) => void;
}

let pendingResolve: PendingResolve | null = null;

export function createConfirmDialog(): ConfirmDialogApi {
  function init(): void {
    const overlay = document.getElementById("confirm-dialog");
    const okBtn = document.getElementById("confirm-ok");
    const cancelBtn = document.getElementById("confirm-cancel");
    const msgEl = document.getElementById("confirm-message");

    if (!overlay || !okBtn || !cancelBtn || !msgEl) return;

    const closeAndResolve = (value: boolean): void => {
      overlay.classList.add("hidden");
      pendingResolve?.resolve(value);
      pendingResolve = null;
    };

    okBtn.onclick = () => closeAndResolve(true);
    cancelBtn.onclick = () => closeAndResolve(false);

    // Keyboard support: Escape cancels, Enter confirms
    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeAndResolve(false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        closeAndResolve(true);
      }
    });
  }

  function ask(message: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const overlay = document.getElementById("confirm-dialog");
      const msgEl = document.getElementById("confirm-message");
      if (!overlay || !msgEl) {
        resolve(true);
        return;
      }

      // Reject previous pending call if any (prevents race condition)
      if (pendingResolve) {
        pendingResolve.reject(new Error("Confirm dialog cancelled"));
      }

      pendingResolve = { resolve, reject };

      msgEl.textContent = message;
      overlay.classList.remove("hidden");
    });
  }

  return { init, ask };
}
